import {DVIRFormDefaultValues, DVIRFormProps, DVIRFormRawValues, DVIRFormType, DVIRFormValues} from "./DVIRForm";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZone,
  CustomFieldProfileItemEntity,
  customFieldProfileItemsSelector,
  DVIREntity,
  getObjectId,
  useGetActiveCustomFieldProfileItemsByCompanyQuery,
  useGetCustomFieldProfileItemsByProfileQuery,
} from "@ots-frontend/ots-data";
import {useEffect, useMemo, useRef, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";

export const toFormDefaultsFromDVIR = (dvir: DVIREntity | undefined): Partial<DVIRFormValues> => {
  return {
    date: adjustFromTimeZoneSafely(dvir?.dttm),
    person: dvir?.personId,
    vehicle: dvir?.vehicleId,
    trailer: dvir?.trailerId,
    vehicleAirCompressor: dvir?.vehicleAirCompressor,
    vehicleAirLines: dvir?.vehicleAirLines,
    vehicleBattery: dvir?.vehicleBattery,
    vehicleBrakes: dvir?.vehicleBrakes,
    vehicleBrakeAccessories: dvir?.vehicleBrakeAccessories,
    vehicleCarburetor: dvir?.vehicleCarburetor,
    vehicleClutch: dvir?.vehicleClutch,
    vehicleDefroster: dvir?.vehicleDefroster,
    vehicleDriveLine: dvir?.vehicleDriveLine,
    vehicleEngine: dvir?.vehicleEngine,
    vehicleFifthWheel: dvir?.vehicleFifthWheel,
    vehicleFrontAxle: dvir?.vehicleFrontAxle,
    vehicleFuelTanks: dvir?.vehicleFuelTanks,
    vehicleHeater: dvir?.vehicleHeater,
    vehicleHorn: dvir?.vehicleHorn,
    vehicleLights: dvir?.vehicleLights,
    vehicleMirrors: dvir?.vehicleMirrors,
    vehicleMuffler: dvir?.vehicleMuffler,
    vehicleOilPressure: dvir?.vehicleOilPressure,
    vehicleOnBoardRecorder: dvir?.vehicleOnBoardRecorder,
    vehicleRadiator: dvir?.vehicleRadiator,
    vehicleRearEnd: dvir?.vehicleRearEnd,
    vehicleReflectors: dvir?.vehicleReflectors,
    vehicleSafetyEquipment: dvir?.vehicleSafetyEquipment,
    vehicleSprings: dvir?.vehicleSprings,
    vehicleStarter: dvir?.vehicleStarter,
    vehicleSteering: dvir?.vehicleSteering,
    vehicleTachograph: dvir?.vehicleTachograph,
    vehicleTires: dvir?.vehicleTires,
    vehicleTransmission: dvir?.vehicleTransmission,
    vehicleWheels: dvir?.vehicleWheels,
    vehicleWindows: dvir?.vehicleWindows,
    vehicleWindshieldWipers: dvir?.vehicleWindshieldWipers,
    vehicleOther: dvir?.vehicleOther,
    trailerBrakeConnections: dvir?.trailerBrakeConnections,
    trailerBrakes: dvir?.trailerBrakes,
    trailerCouplingChains: dvir?.trailerCouplingChains,
    trailerCouplingPin: dvir?.trailerCouplingPin,
    trailerDoors: dvir?.trailerDoors,
    trailerHitch: dvir?.trailerHitch,
    trailerLandingGear: dvir?.trailerLandingGear,
    trailerLights: dvir?.trailerLights,
    trailerRoof: dvir?.trailerRoof,
    trailerSprings: dvir?.trailerSprings,
    trailerTarpaulin: dvir?.trailerTarpaulin,
    trailerTires: dvir?.trailerTires,
    trailerWheels: dvir?.trailerWheels,
    trailerOther: dvir?.trailerOther,
    satisfactory: dvir?.satisfactory,
    comments: dvir?.comments,
    custom: dvir?.customFieldValues,
  };
};

const getInitialValues = (defaultValues: DVIRFormDefaultValues | undefined): DVIRFormRawValues => {
  return {
    date: defaultValues?.date ?? new Date(),
    person: getObjectId(defaultValues?.person),
    vehicle: getObjectId(defaultValues?.vehicle),
    trailer: getObjectId(defaultValues?.trailer),
    vehicleAirCompressor: defaultValues?.vehicleAirCompressor ?? false,
    vehicleAirLines: defaultValues?.vehicleAirLines ?? false,
    vehicleBattery: defaultValues?.vehicleBattery ?? false,
    vehicleBrakes: defaultValues?.vehicleBrakes ?? false,
    vehicleBrakeAccessories: defaultValues?.vehicleBrakeAccessories ?? false,
    vehicleCarburetor: defaultValues?.vehicleCarburetor ?? false,
    vehicleClutch: defaultValues?.vehicleClutch ?? false,
    vehicleDefroster: defaultValues?.vehicleDefroster ?? false,
    vehicleDriveLine: defaultValues?.vehicleDriveLine ?? false,
    vehicleEngine: defaultValues?.vehicleEngine ?? false,
    vehicleFifthWheel: defaultValues?.vehicleFifthWheel ?? false,
    vehicleFrontAxle: defaultValues?.vehicleFrontAxle ?? false,
    vehicleFuelTanks: defaultValues?.vehicleFuelTanks ?? false,
    vehicleHeater: defaultValues?.vehicleHeater ?? false,
    vehicleHorn: defaultValues?.vehicleHorn ?? false,
    vehicleLights: defaultValues?.vehicleLights ?? false,
    vehicleMirrors: defaultValues?.vehicleMirrors ?? false,
    vehicleMuffler: defaultValues?.vehicleMuffler ?? false,
    vehicleOilPressure: defaultValues?.vehicleOilPressure ?? false,
    vehicleOnBoardRecorder: defaultValues?.vehicleOnBoardRecorder ?? false,
    vehicleRadiator: defaultValues?.vehicleRadiator ?? false,
    vehicleRearEnd: defaultValues?.vehicleRearEnd ?? false,
    vehicleReflectors: defaultValues?.vehicleReflectors ?? false,
    vehicleSafetyEquipment: defaultValues?.vehicleSafetyEquipment ?? false,
    vehicleSprings: defaultValues?.vehicleSprings ?? false,
    vehicleStarter: defaultValues?.vehicleStarter ?? false,
    vehicleSteering: defaultValues?.vehicleSteering ?? false,
    vehicleTachograph: defaultValues?.vehicleTachograph ?? false,
    vehicleTires: defaultValues?.vehicleTires ?? false,
    vehicleTransmission: defaultValues?.vehicleTransmission ?? false,
    vehicleWheels: defaultValues?.vehicleWheels ?? false,
    vehicleWindows: defaultValues?.vehicleWindows ?? false,
    vehicleWindshieldWipers: defaultValues?.vehicleWindshieldWipers ?? false,
    vehicleOther: defaultValues?.vehicleOther ?? false,
    trailerBrakeConnections: defaultValues?.trailerBrakeConnections ?? false,
    trailerBrakes: defaultValues?.trailerBrakes ?? false,
    trailerCouplingChains: defaultValues?.trailerCouplingChains ?? false,
    trailerCouplingPin: defaultValues?.trailerCouplingPin ?? false,
    trailerDoors: defaultValues?.trailerDoors ?? false,
    trailerHitch: defaultValues?.trailerHitch ?? false,
    trailerLandingGear: defaultValues?.trailerLandingGear ?? false,
    trailerLights: defaultValues?.trailerLights ?? false,
    trailerRoof: defaultValues?.trailerRoof ?? false,
    trailerSprings: defaultValues?.trailerSprings ?? false,
    trailerTarpaulin: defaultValues?.trailerTarpaulin ?? false,
    trailerTires: defaultValues?.trailerTires ?? false,
    trailerWheels: defaultValues?.trailerWheels ?? false,
    trailerOther: defaultValues?.trailerOther ?? false,
    satisfactory: defaultValues?.satisfactory ?? false,
    comments: defaultValues?.comments,
    custom: defaultValues?.custom?.reduce((accum, customFieldValue) => {
      accum[customFieldValue.customFieldProfileItemId] = (customFieldValue.stringValue ?? customFieldValue.numberValue ?? customFieldValue.booleanValue ?? customFieldValue.temporalValue ?? customFieldValue.refObjectIdValue)
      return accum
    }, {} as Record<string, any>),
  }
}

export type UseCustomFieldProfileFormResult = {
  form: DVIRFormType,
  customFields: CustomFieldProfileItemEntity[]
  customFieldsLoaded: boolean
}

export type UseDVIRFormProps = Pick<DVIRFormProps, 'defaultValues' | 'options'> & {}

export function useDVIRForm(props: UseDVIRFormProps): UseCustomFieldProfileFormResult {
  const {
    defaultValues,
    options,
  } = props

  const {t} = useTranslation(["common"])

  const [customFields, setCustomFields] = useState<CustomFieldProfileItemEntity[]>([]);
  const [customFieldsLoaded, setCustomFieldsLoaded] = useState(false);

  const customFieldsEverLoadedRef = useRef(false);

  const form = useForm({
    initialValues: getInitialValues({
      ...defaultValues,
    }),
    validate: {
      date: (value) => value ? undefined : t("common.validation.required", "Required"),
      person: (value) => value ? undefined : t("common.validation.required", "Required"),
      vehicle: (value, values) => {
        if (!value) {
          for (const key of Object.keys(values)) {
            if (key.startsWith("vehicle")) {
              if (values[key] === true) {
                return t("common.validation.required", "Required")
              }
            }
          }
        }
        return undefined
      },
      trailer: (value, values) => {
        if (!value) {
          for (const key of Object.keys(values)) {
            if (key.startsWith("trailer")) {
              if (values[key] === true) {
                return t("common.validation.required", "Required")
              }
            }
          }
        }
        return undefined
      },
    },
  })

  const selectCustomFieldProfileItems = useMemo(() => {
    return customFieldProfileItemsSelector();
  }, []);

  const dttm = useMemo(() => {
    return adjustToTimeZone(form.values.date ?? Date.now()).getTime()
  }, [form.values.date]);

  const companyId = options?.companyId;

  const usingProfile = !!options?.customFieldProfileId;

  const {
    items: byCompanyItems,
    isLoading: byCompanyLoading,
    isFetching: byCompanyFetching,
    isSuccess: byCompanySuccess,
    isUninitialized: byCompanyUninit,
  } = useGetActiveCustomFieldProfileItemsByCompanyQuery(
    companyId ? {companyId, objectType: "DVIR", dttm} : skipToken,
    {
      selectFromResult: (result) => ({
        ...result,
        items: selectCustomFieldProfileItems(result),
      }),
    }
  );

  const {
    items: byProfileItems,
    isLoading: byProfileLoading,
    isFetching: byProfileFetching,
    isSuccess: byProfileSuccess,
    isUninitialized: byProfileUninit,
  } = useGetCustomFieldProfileItemsByProfileQuery(
    options?.customFieldProfileId ? options.customFieldProfileId : skipToken,
    {
      selectFromResult: (result) => ({
        ...result,
        items: selectCustomFieldProfileItems(result),
      }),
    }
  );

  const activeQueryLoaded = usingProfile
    ? byProfileSuccess || (!byProfileUninit && !byProfileLoading && !byProfileFetching)
    : byCompanySuccess || (!byCompanyUninit && !byCompanyLoading && !byCompanyFetching);

  useEffect(() => {
    const items: CustomFieldProfileItemEntity[] = [];
    if (usingProfile) {
      items.push(...(byProfileItems ?? []));
    } else {
      items.push(...(byCompanyItems ?? []));
    }

    setCustomFields(items);

    // Mark loaded once the active query has finished at least once
    if (activeQueryLoaded) {
      customFieldsEverLoadedRef.current = true;
      setCustomFieldsLoaded(true);
    } else {
      setCustomFieldsLoaded(false);
    }
  }, [usingProfile, byCompanyItems, byProfileItems, activeQueryLoaded]);

  return {
    form: form,
    customFields: customFields,
    customFieldsLoaded: customFieldsLoaded,
  }
}
